import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { Check, Clock, X } from 'lucide-react';
import type { PaymentReturnKind, TipOutcome } from '../../utils/tipReturn';

const popIn = keyframes`0% { opacity: 0; transform: scale(0.6); } 60% { transform: scale(1.08); } 100% { opacity: 1; transform: scale(1); }`;

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export interface TipThanksProps {
    /** Null when a garbled return URL lost it. Copy degrades, never breaks. */
    recipientName?: string | null;
    /** What the recipient receives, in cents. Null degrades the same way. */
    netCents?: number | null;
    /** Defaults to `succeeded`, which is the in-sheet card path. */
    outcome?: TipOutcome;
    /** A money-backed song request ends here too, with its own words (ADR-0038). */
    kind?: PaymentReturnKind;
    songTitle?: string | null;
}

/**
 * The end of a tip, in one beat.
 *
 * Shared by the two places a tip can finish, which is the whole reason it is a
 * component rather than markup inside the sheet: a card confirms in place and
 * ends here, while a redirect method (Cash App and friends) ends here on a
 * fresh page load after the sheet is long gone. Those were always going to be
 * two renderings of one idea, and two copies is how they drift into telling a
 * fan different things about the same payment.
 *
 * Three outcomes, not two. `processing` exists because a redirect method can
 * settle asynchronously, and the copy for it must not claim the money arrived:
 * it says what will happen and that nothing is required, so a fan does not pay
 * twice to be sure.
 *
 * Copy degrades rather than breaking. The name and amount arrive from a URL a
 * fan can edit, so either may be missing; the sentence is written twice rather
 * than interpolating a blank, because "just got ." is worse than a generic
 * confirmation.
 */
export default function TipThanks({
    recipientName, netCents, outcome = 'succeeded', kind = 'tip', songTitle,
}: TipThanksProps): React.ReactElement {
    const theme = useTheme();
    const name = recipientName?.trim() || null;
    const amount = netCents && netCents > 0 ? fmt(netCents) : null;
    const song = songTitle?.trim() || null;

    const tone = outcome === 'failed' ? theme.palette.error.main : theme.palette.secondary.main;
    const Icon = outcome === 'succeeded' ? Check : outcome === 'processing' ? Clock : X;

    let headline: string;
    let body: string;

    // A request's "success" is a hold, not a charge: the card is only charged
    // when the act plays the song. The copy has to say exactly that, or a fan whose
    // request is passed on thinks they paid for nothing.
    if (kind === 'song_request') {
        const what = song ? `"${song}"` : 'your request';
        const who = name ?? 'The act';
        if (outcome === 'succeeded') {
            headline = 'Request sent';
            // Not "your card": this is also the screen a Cash App payment returns to.
            body = `${who} will see ${what}. You're only charged if they play it.`;
        } else if (outcome === 'processing') {
            headline = 'Your request is on its way';
            body = `It reaches ${name ?? 'the act'} once your payment clears. You're only charged if they play it.`;
        } else {
            headline = 'That didn\'t go through';
            body = 'Nothing was charged. You can try again whenever you like.';
        }
    } else if (outcome === 'succeeded') {
        headline = 'Thanks for the love';
        body = name && amount
            ? `${name} just got ${amount}. They'll see your tip light up the room.`
            : name
                ? `${name} just got your tip. They'll see it light up the room.`
                : 'Your tip is in. They\'ll see it light up the room.';
    } else if (outcome === 'processing') {
        headline = 'Your tip is on its way';
        body = name && amount
            ? `${name} gets ${amount} as soon as it clears. Nothing else for you to do.`
            : name
                ? `${name} gets your tip as soon as it clears. Nothing else for you to do.`
                : 'It lands as soon as it clears. Nothing else for you to do.';
    } else {
        headline = 'That didn\'t go through';
        body = 'Nothing left your account. You can try again whenever you like.';
    }

    return (
        <Box sx={{ maxWidth: 460, mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 5 }}>
            <Box
                sx={{
                    width: 88,
                    height: 88,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tone,
                    bgcolor: alpha(tone, 0.16),
                    border: `1px solid ${alpha(tone, 0.4)}`,
                    animation: `${popIn} 420ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
            >
                <Icon size={42} strokeWidth={2.6} />
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.15 }}>
                {headline}
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 320 }}>
                {body}
            </Typography>
        </Box>
    );
}
